import { DateTime } from "luxon";
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { FormControl, FormGroup } from "@angular/forms";
import { MatPaginator } from "@angular/material/paginator";
import { ProjectService } from "src/app/Services/project.service";
import { SharedService } from "src/app/Services/sharedService";
import { config } from "src/app/config";
import moment from "moment";
import { HttpClient, HttpResponse } from "@angular/common/http";
import { SweetAlertService } from "src/app/Services/sweet-alert.service";
import swal from 'sweetalert2';
import JSZip from "jszip";
import { saveAs } from 'file-saver';
import { DummyDataService } from "src/app/Services/dummy-data.service";
import { ExampleWorkdayService } from "src/app/example-workday/example-workday.service";
import { TokenEncryptionService } from "src/app/Services/token-encryption.service";
import { Employee } from "src/app/interface/employee";
import { ScreenshotTimeService } from "src/app/Services/screenshot-time.service";
import { ScreenshotFancyboxService } from "src/app/Services/screenshot-fancybox.service";
import { EntitlementsService } from "src/app/Services/entitlements.service";
import { Subject, of, timeout, forkJoin } from "rxjs";
import { catchError, finalize, takeUntil } from "rxjs/operators";

@Component({
  selector: "app-screenshots",
  templateUrl: "./screenshots.component.html",
  styleUrls: ["./screenshots.component.css"],
  standalone: false
})
export class ScreenshotsComponent implements OnInit, OnDestroy {
  @ViewChild("EmpPaginator", { static: false }) EmpPaginator: MatPaginator;
  PhotoList: any = [];
  userList = [];
  filteredUserList = [];
  total_ss = 0;
  pageSize = 25;
  offset = 0;
  currentPage = 1;
  isloading = true;
  loading = false;
  isAdmin: boolean = false;
  userImageClass = 'avatar-img';
  filePath = config.baseMediaUrl;
  total_pages = 0;
  selectedDateAndTime: { startDate: moment.Moment, endDate: moment.Moment };
  employeeMultiCtrl: FormControl = new FormControl('');
  employeeSearchCtrl: FormControl = new FormControl('');
  serverDate: any = new Date();
  maxDate: any;
  minDate: any;
  screenshotDateRange = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });
  screenshotStartTimeStr = '00:00:00';
  screenshotEndTimeStr = '23:59:59';

  skeletonLoaders = Array(8).fill(0);
  skeletonTheme = {
    'border-radius': '12px',
    height: '350px',
    margin: '0px',
    width: '100%'
  };
  pagedList: any[] = []; // The currently displayed page of data
  pageSearchTerm: string = ''; // Search term for page filtering

  // Conduct project integration & filtering
  isConductIntegrated = false;
  conductTimelogs: any[] = [];
  parsedConductIntervals: { startUtcMs: number; endUtcMs: number; projectName: string; taskTitle: string; raw: any }[] = [];
  availableProjects: { name: string; totalHours?: string; totalSeconds?: number }[] = [];
  projectFilterCtrl: FormControl = new FormControl('all');
  rawPhotoList: any[] = [];
  allLoadedPhotos: any[] = [];
  filteredPhotos: any[] = [];
  serverTotalSs: number = 0;
  private lastConductFetchKey: string = '';


  // For demo/dummy data
  isShowingDemoData: boolean = false;
  showExamplePreview = false;
  ownWorkdayReady = false;

  /** Company shift IANA zone for interpreting naive screenshot times */
  shiftTimezone: string | null = null;
  /** Explains locale + work-region behavior for international users */
  regionHint: string | null = null;
  /** Cached for mat-date-range-input [min]/[max] — avoid new Date() every change detection. */
  screenshotMatMinDate: Date | null = null;
  screenshotMatMaxDate: Date | null = null;

  /** Wait after time edits before refetching (native time UI can fire many events). */
  private readonly screenshotTimeFilterDebounceMs = 500;
  private screenshotTimeFilterDebounceId: ReturnType<typeof setTimeout> | null = null;
  private readonly destroy$ = new Subject<void>();
  private readonly listRequestTimeoutMs = 25000;
  private readonly maxPageSize = 50;
  /** Ignore screenshot list responses that were overtaken by a newer filter/fetch. */
  private screenshotListRequestId = 0;

  private refreshScreenshotMatDateBounds(): void {
    this.screenshotMatMinDate = this.toMaterialDate(this.minDate);
    this.screenshotMatMaxDate = this.toMaterialDate(this.maxDate);
  }

  private toMaterialDate(v: any): Date | null {
    if (v == null || v === '') {
      return null;
    }
    const d = v instanceof Date ? new Date(v.getTime()) : new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private patchScreenshotDateRangeFromMoments(): void {
    if (!this.selectedDateAndTime?.startDate || !this.selectedDateAndTime?.endDate) {
      return;
    }
    this.screenshotDateRange.patchValue(
      {
        start: this.selectedDateAndTime.startDate.clone().startOf('day').toDate(),
        end: this.selectedDateAndTime.endDate.clone().startOf('day').toDate(),
      },
      { emitEvent: false }
    );
    this.screenshotStartTimeStr = this.selectedDateAndTime.startDate.format('HH:mm:ss');
    this.screenshotEndTimeStr = this.selectedDateAndTime.endDate.format('HH:mm:ss');
  }

  private parseDateAndTimeString(d: Date, timeStr: string): moment.Moment {
    const parts = (timeStr || '00:00:00').split(':');
    const h = Math.min(23, Math.max(0, parseInt(parts[0], 10) || 0));
    const m = Math.min(59, Math.max(0, parseInt(parts[1], 10) || 0));
    const s = Math.min(59, Math.max(0, parseInt(parts[2], 10) || 0));
    return moment(d).startOf('day').hour(h).minute(m).second(s).millisecond(0);
  }

  onScreenshotMatFilterChange(): void {
    this.clearPendingScreenshotTimeFilterDebounce();
    this.applyScreenshotFilterFromControls();
  }

  /** Debounced refetch so adjusting start/end time does not hit the API on every step. */
  scheduleScreenshotTimeFilterApply(): void {
    this.clearPendingScreenshotTimeFilterDebounce();
    this.screenshotTimeFilterDebounceId = setTimeout(() => {
      this.screenshotTimeFilterDebounceId = null;
      this.applyScreenshotFilterFromControls();
    }, this.screenshotTimeFilterDebounceMs);
  }

  private applyScreenshotFilterFromControls(): void {
    const startD = this.screenshotDateRange.value.start;
    const endD = this.screenshotDateRange.value.end;
    if (!startD || !endD) {
      return;
    }
    this.selectedDateAndTime = {
      startDate: this.parseDateAndTimeString(startD, this.screenshotStartTimeStr),
      endDate: this.parseDateAndTimeString(endD, this.screenshotEndTimeStr),
    };
    this.change(null);
  }

  private clearPendingScreenshotTimeFilterDebounce(): void {
    if (this.screenshotTimeFilterDebounceId != null) {
      clearTimeout(this.screenshotTimeFilterDebounceId);
      this.screenshotTimeFilterDebounceId = null;
    }
  }

  // Profile image methods
  getProfileImageUrl(user: any): string | null {
    if (!user) return null;

    // Check for profile_image_url in meta first (from API response)
    if (user.meta && user.meta.profile_image_url) {
      return user.meta.profile_image_url;
    }

    // Check for profile_image in meta (alternative custom field)
    if (user.meta && user.meta.profile_image) {
      return user.meta.profile_image;
    }

    // Fallback to avatar_urls (WordPress default)
    if (user.avatar_urls && user.avatar_urls[96]) {
      return user.avatar_urls[96];
    }

    return null;
  }

  getDisplayImageUrl(user: any): string {
    // Return profile image if available, otherwise return default placeholder
    return this.getProfileImageUrl(user) || this.getDefaultImageUrl();
  }

  getDefaultImageUrl(): string {
    // Return a data URI for a simple default placeholder icon
    return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 24 24' fill='%23B3B3B3'%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/%3E%3C/svg%3E";
  }

  onImageError(event: any, user: any): void {
    // Hide the image and show initials if available
    const imgElement = event.target as HTMLImageElement;
    imgElement.style.display = 'none';

    // Find the initials element in the same wrapper (works for both dropdown and trigger)
    const wrapper = imgElement.parentElement;
    if (wrapper) {
      const initialsElement = wrapper.querySelector('.dropdown-profile-initials, .trigger-profile-initials') as HTMLElement;
      if (initialsElement) {
        initialsElement.style.display = 'flex';
      }
    }
  }

  getInitials(user: any): string {
    if (!user || !user.name) return 'U';

    const nameParts = user.name.trim().split(' ');
    if (nameParts.length >= 2) {
      return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
    } else if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }

    return 'U';
  }

  getSelectedUser(): any {
    const selectedId = this.employeeMultiCtrl.value;
    if (!selectedId) return null;
    return this.userList.find((user: any) => user.id === selectedId);
  }

  constructor(
    private _projectService: ProjectService,
    private _sharedServices: SharedService,
    private http: HttpClient,
    private sweetAlertService: SweetAlertService,
    private _dummyDataService: DummyDataService,
    private tokenEncryptionService: TokenEncryptionService,
    private screenshotTime: ScreenshotTimeService,
    private screenshotFancybox: ScreenshotFancyboxService,
    private entitlementsService: EntitlementsService,
    private exampleWorkday: ExampleWorkdayService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    const currentUser =
      this.tokenEncryptionService.getEncryptedItem<any>('logedInUser') ||
      this.tokenEncryptionService.getEncryptedItem<any>('user');
    if (currentUser && currentUser.role) {
      this.isAdmin = currentUser.role.includes('company');
    }
  }

  ngOnInit(): void {
    this.showExamplePreview = this.exampleWorkday.shouldOfferExamplePreviews();
    this.ownWorkdayReady = this.exampleWorkday.isOwnWorkdayReady();
    const params = this.route.snapshot.queryParams;
    this.applyEmployeeIdFromQuery(params['employeeId']);
    if (params['page']) {
      const page = Number(params['page']);
      this.currentPage = Number.isFinite(page) && page > 0 ? page : 1;
    }
    if (params['pageSize']) {
      this.pageSize = this.sanitizePageSize(Number(params['pageSize']));
      this.offset = (this.currentPage - 1) * this.pageSize;
    }

    this.entitlementsService.syncFromSession();
    this.checkConductIntegration();
    if (params['project']) {
      this.projectFilterCtrl.setValue(String(params['project']).toLowerCase(), { emitEvent: false });
    }
    this.loggedInUserDetails();

    this.initializeDateRange();
    this.isShowDummyUsers();

    // Check if we should show demo data
    const isTour = localStorage.getItem('isTour');
    if (isTour === 'true') {
      this.isShowingDemoData = true;
      this._dummyDataService.setDemoDataVisible(true);
    }

    // Do not block the grid on shift timezone — a slow/hung shifts call left skeletons forever.
    this.getUserList();

    const companyId = this.tokenEncryptionService.getEncryptedItem<any>('logedInUser')?.company_id;
    this.screenshotTime
      .getShiftTimezoneForCompany(companyId)
      .pipe(takeUntil(this.destroy$), timeout({ first: 8000, with: () => of(null) }))
      .subscribe((tz) => {
        this.shiftTimezone = tz;
        this.regionHint = this.screenshotTime.getScreenshotsRegionHint(tz);
        // Enrich already-loaded cards once zone arrives (no full refetch needed for display).
        if (this.rawPhotoList?.length) {
          this.rawPhotoList = this.rawPhotoList.map((ele: any) => ({
            ...ele,
            ...(this.screenshotTime.getEnrichedScreenshotTime(ele.ist_time || ele.captured_at, this.shiftTimezone) ??
              {}),
          }));
          this.matchScreenshotsToConductLogs();
          this.applyProjectFilter();
        } else if (this.PhotoList?.length) {
          this.PhotoList = this.PhotoList.map((ele: any) => ({
            ...ele,
            ...(this.screenshotTime.getEnrichedScreenshotTime(ele.ist_time || ele.captured_at, this.shiftTimezone) ??
              {}),
          }));
        }
      });

    // Listen for demo mode toggles
    this._sharedServices.dashboardDemoOptInRefresh$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getUserList();
      });

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((qp) => {
      const nextProj = qp['project'] ? String(qp['project']).toLowerCase() : 'all';
      if ((this.projectFilterCtrl.value || 'all').toLowerCase() !== nextProj) {
        this.projectFilterCtrl.setValue(nextProj, { emitEvent: false });
        this.applyProjectFilter();
      }

      const nextId = qp['employeeId'];
      const current = this.employeeMultiCtrl.value;
      if (
        nextId != null &&
        nextId !== '' &&
        String(current ?? '') !== String(Number(nextId) || nextId)
      ) {
        this.applyEmployeeIdFromQuery(nextId);
        this.currentPage = 1;
        this.offset = 0;
        this.getLastScreenshots();
      }
    });
  }

  ngOnDestroy(): void {
    this.clearPendingScreenshotTimeFilterDebounce();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyEmployeeIdFromQuery(raw: unknown): void {
    if (raw == null || raw === '') {
      return;
    }
    const asNum = Number(raw);
    this.employeeMultiCtrl.setValue(Number.isFinite(asNum) && asNum > 0 ? asNum : raw);
  }

  private sanitizePageSize(value: number): number {
    if (!Number.isFinite(value) || value <= 0) {
      return 25;
    }
    return Math.min(Math.floor(value), this.maxPageSize);
  }

  onEmployeeSelected(employeeId: string | number): void {
    this.employeeMultiCtrl.setValue(employeeId);
    this.projectFilterCtrl.setValue('all', { emitEvent: false });
    this.lastConductFetchKey = '';
    this.currentPage = 1;
    this.offset = 0;
    this.pagedList = [];
    this.total_ss = 0;
    this.isloading = true;
    this.updateQueryParams();
    this.getLastScreenshots();
  }

  updateQueryParams() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        employeeId: this.employeeMultiCtrl.value || null,
        project: this.projectFilterCtrl.value && this.projectFilterCtrl.value !== 'all' ? this.projectFilterCtrl.value : null,
        startDate: this.selectedDateAndTime?.startDate ? this.selectedDateAndTime.startDate.format('YYYY-MM-DD') : null,
        endDate: this.selectedDateAndTime?.endDate ? this.selectedDateAndTime.endDate.format('YYYY-MM-DD') : null,
        startTime: this.screenshotStartTimeStr !== '00:00:00' ? this.screenshotStartTimeStr : null,
        endTime: this.screenshotEndTimeStr !== '23:59:59' ? this.screenshotEndTimeStr : null,
        page: this.currentPage > 1 ? this.currentPage : null,
        pageSize: this.pageSize !== 25 ? this.pageSize : null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  async isShowDummyUsers() {
    // Only prepare sample screenshots after we know the account has no real users.
    // Actual switch happens in getUserList via checkUserIsUseDummyData.
    this.isShowingDemoData = false;
  }

  initializeDateRange() {
    const params = this.route.snapshot.queryParams;
    let start, end;
    let repairedFromQuery = false;

    if (params['startDate'] && params['endDate']) {
      start = moment(params['startDate']);
      if (params['startTime']) {
        const timeParts = params['startTime'].split(':');
        start.hour(Number(timeParts[0]) || 0).minute(Number(timeParts[1]) || 0).second(Number(timeParts[2]) || 0);
      } else {
        start.startOf('day');
      }

      end = moment(params['endDate']);
      if (params['endTime']) {
        const timeParts = params['endTime'].split(':');
        end.hour(Number(timeParts[0]) || 0).minute(Number(timeParts[1]) || 0).second(Number(timeParts[2]) || 0);
      } else {
        end.endOf('day');
      }

      // Hard refresh restores dates written when company/server "today" was still yesterday
      // in the admin's timezone (trial companies often use UTC/US shift TZ around IST midnight).
      if (this.isTimezoneShiftedDefaultRange(start, end)) {
        const localDefault = this.defaultScreenshotDateRange();
        start = localDefault.startDate;
        end = localDefault.endDate;
        repairedFromQuery = true;
      }
    } else {
      const localDefault = this.defaultScreenshotDateRange();
      start = localDefault.startDate;
      end = localDefault.endDate;
    }

    this.selectedDateAndTime = {
      startDate: start,
      endDate: end
    };
    this.patchScreenshotDateRangeFromMoments();
    if (repairedFromQuery) {
      this.updateQueryParams();
    }
  }

  async getUserList() {
    // Force refresh to bypass cache, like dashboard does
    this._projectService.getUserList(0, 0).pipe(
      takeUntil(this.destroy$),
      timeout({ first: this.listRequestTimeoutMs, with: () => of([]) })
    ).subscribe(
      async (res: any) => {
        if (res && Array.isArray(res)) {
          this.userList = res;
          this.filteredUserList = res.slice();

          const isShowDemoData = await this._dummyDataService.checkIsAvailableDemoData<Employee[]>('EmployeeList');
          if (this._sharedServices.checkUserIsUseDummyData(isShowDemoData, this.userList)) {
            this.isShowingDemoData = true;
            this._dummyDataService.setDemoDataVisible(true);
            this.generateDummyScreenshots();
            this.isloading = false;
            return;
          }

          this.isShowingDemoData = false;
          this._dummyDataService.setDemoDataVisible(false);
          if (this.userList.length > 0) {
            this._dummyDataService.clearLegacySampleMode();
          }
          this.getLastScreenshots();
        } else {
          console.warn('⚠️ User list response is not an array:', res);
          this.isloading = false;
        }
      },
      (error) => {
        console.error("❌ Error fetching user list:", error);
        this.isloading = false;
      }
    );
  }

  filterEmployees() {
    const searchValue = this.employeeSearchCtrl.value?.toLowerCase() || '';
    const list = Array.isArray(this.userList) ? this.userList : [];

    if (!searchValue) {
      this.filteredUserList = list.slice();
      return;
    }

    this.filteredUserList = list.filter((user: any) => {
      const name = user.name?.toLowerCase() || '';
      const email = user.email?.toLowerCase() || '';
      return name.includes(searchValue) || email.includes(searchValue);
    });
  }

  async getLastScreenshots() {
    this.isloading = true;
    this.PhotoList = [];

    // Check if we're showing demo data and generate dummy screenshots
    if (this.isShowingDemoData) {
      this.generateDummyScreenshots();
      this.isloading = false;
      return;
    }

    const selectedEmployee = this.employeeMultiCtrl.value;
    let startDate: string | null = null;
    let endDate: string | null = null;
    if (this.selectedDateAndTime?.startDate && this.selectedDateAndTime?.endDate) {
      // Pass the exact date-time picked by the user instead of forcing full-day bounds.
      startDate = this.selectedDateAndTime.startDate.format('YYYY-MM-DD HH:mm:ss');
      endDate = this.selectedDateAndTime.endDate.format('YYYY-MM-DD HH:mm:ss');
    }

    this.loadConductTimelogs(startDate, endDate);

    this.pageSize = this.sanitizePageSize(this.pageSize);
    const safePageSize = this.pageSize;
    const requestId = ++this.screenshotListRequestId;
    const isProjectFilterActive = (this.projectFilterCtrl.value || 'all').toLowerCase() !== 'all';

    this._projectService
      .getlastFifteenPhoto(selectedEmployee, safePageSize, this.offset, this.currentPage, startDate, endDate)
      .pipe(
        takeUntil(this.destroy$),
        timeout({ first: this.listRequestTimeoutMs }),
        finalize(() => {
          if (requestId === this.screenshotListRequestId && !isProjectFilterActive) {
            this.isloading = false;
          }
        })
      )
      .subscribe(
        (res: HttpResponse<any>) => {
          if (requestId !== this.screenshotListRequestId) {
            return;
          }

          let photos = Array.isArray(res.body?.data) ? res.body.data : [];
          if (res.body?.pagination) {
            this.serverTotalSs = Number(res.body?.pagination?.total_images) || 0;
            this.total_ss = this.serverTotalSs;
            const apiPerPage = Number(res.body?.pagination?.per_page);
            this.pageSize = this.sanitizePageSize(
              Number.isFinite(apiPerPage) && apiPerPage > 0 ? apiPerPage : this.pageSize || 25
            );
            this.currentPage = Number(res.body?.pagination?.current_page) || 1;
            const apiTotalPages = Number(res.body?.pagination?.total_pages);
            this.total_pages =
              Number.isFinite(apiTotalPages) && apiTotalPages > 0
                ? apiTotalPages
                : Math.max(1, Math.ceil(this.total_ss / this.pageSize) || 1);
            this.pagedList = this.buildPageNumberList(this.total_pages);
          }

          // If a project filter is active and there are multiple pages, fetch the remaining pages
          if (isProjectFilterActive && this.total_pages > 1) {
            const requests = [];
            const maxPages = Math.min(this.total_pages, 10);
            for (let p = 2; p <= maxPages; p++) {
              requests.push(
                this._projectService
                  .getlastFifteenPhoto(selectedEmployee, safePageSize, (p - 1) * safePageSize, p, startDate, endDate)
                  .pipe(catchError(() => of(null)))
              );
            }
            forkJoin(requests)
              .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                  if (requestId === this.screenshotListRequestId) {
                    this.isloading = false;
                  }
                })
              )
              .subscribe((extraResponses: any[]) => {
                if (requestId !== this.screenshotListRequestId) return;
                for (const r of extraResponses) {
                  const morePhotos = Array.isArray(r?.body?.data) ? r.body.data : [];
                  photos.push(...morePhotos);
                }
                this.processReceivedPhotos(photos, requestId);
              });
            return;
          }

          this.processReceivedPhotos(photos, requestId);
        },
        (error) => {
          if (requestId !== this.screenshotListRequestId) {
            return;
          }
          console.error("Error fetching screenshots:", error);
          this.rawPhotoList = [];
          this.allLoadedPhotos = [];
          this.PhotoList = [];
          this.isloading = false;
        }
      );
  }

  /**
   * Page-jump dropdown options. Cap size so a bad total_images / per_page never freezes the tab.
   */
  private buildPageNumberList(totalPages: number): number[] {
    const safeTotal = Math.min(Math.max(1, Math.floor(totalPages) || 1), 500);
    return Array.from({ length: safeTotal }, (_, i) => i + 1);
  }

  change(event: any) {
    if (this.selectedDateAndTime && this.selectedDateAndTime.startDate && this.selectedDateAndTime.endDate) {
      if (this.EmpPaginator) {
        this.EmpPaginator.firstPage();
      }
      this.offset = 0;
      this.currentPage = 1;
      this.updateQueryParams();
      this.getLastScreenshots();
    }
  }

  onPageChange(event: any) {
    if (event.pageSize !== this.pageSize) {
      const nextSize = Number(event.pageSize);
      this.pageSize = Number.isFinite(nextSize) && nextSize > 0 ? nextSize : 25;
      this.currentPage = 1;
      this.offset = 0;
      this.pagedList = [];
    } else {
      this.currentPage = event.pageIndex + 1;
      this.offset = event.pageIndex * this.pageSize;
    }

    this.updateQueryParams();

    const isProjectFilterActive = (this.projectFilterCtrl.value || 'all').toLowerCase() !== 'all';
    if (isProjectFilterActive && this.allLoadedPhotos && this.allLoadedPhotos.length > 0) {
      this.applyProjectFilter();
    } else {
      this.getLastScreenshots();
    }
  }

  goToPage(pageIndex: number) {
    const page = Math.max(1, Number(pageIndex) || 1);
    this.currentPage = page;
    this.offset = (page - 1) * (this.pageSize || 25);
    if (this.EmpPaginator) {
      this.EmpPaginator.pageIndex = page - 1;
    }
    this.updateQueryParams();

    const isProjectFilterActive = (this.projectFilterCtrl.value || 'all').toLowerCase() !== 'all';
    if (isProjectFilterActive && this.allLoadedPhotos && this.allLoadedPhotos.length > 0) {
      this.applyProjectFilter();
    } else {
      this.getLastScreenshots();
    }

    // Scroll to top smoothly when jumping to a page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  getFilteredPages(): number[] {
    if (!this.pageSearchTerm || this.pageSearchTerm.trim() === '') {
      return this.pagedList;
    }

    const searchTerm = this.pageSearchTerm.toLowerCase().trim();
    return this.pagedList.filter(pageNum => {
      return pageNum.toString().includes(searchTerm);
    });
  }

  getUserName(userId: string | number): string {
    const parsedId = typeof userId === 'number' ? userId : parseInt(userId);
    const user = this.userList.find(u => u.id == parsedId);
    return user ? user.name : 'Unknown User';
  }

  getUserfromUserList(userId: string | number): any {
    if (!userId && userId !== 0) {
      return null;
    }

    const parsedId = typeof userId === 'number' ? userId : parseInt(userId as string);
    const selectedUser = this.userList.filter((ele: any) => {
      return ele.id == parsedId;
    });

    return selectedUser.length > 0 ? selectedUser[0] : null;
  }

  onAppIconError(image: any): void {
    image._appIconErr = true;
  }

  private checkConductIntegration(): void {
    const user = this.tokenEncryptionService.getEncryptedItem<any>('user');
    const loggedInUser = this.tokenEncryptionService.getEncryptedItem<any>('logedInUser');
    const companyId = Number(loggedInUser?.company_id ?? user?.company_id ?? user?.id ?? 0);
    if (!companyId) return;

    this._projectService.getConductIntegrationStatus(companyId).pipe(
      takeUntil(this.destroy$),
      catchError(() => of(null))
    ).subscribe((statusRes: any) => {
      this.isConductIntegrated = !!(statusRes?.is_integrated || statusRes?.status === 'active' || statusRes?.connected);
    });
  }

  private loadConductTimelogs(startDate: string | null, endDate: string | null): void {
    const user = this.tokenEncryptionService.getEncryptedItem<any>('user');
    const loggedInUser = this.tokenEncryptionService.getEncryptedItem<any>('logedInUser');
    const companyId = Number(loggedInUser?.company_id ?? user?.company_id ?? user?.id ?? 0);
    const loggedInUserId = Number(loggedInUser?.id ?? user?.id ?? 0);
    if (!companyId) return;

    const startYmd = startDate ? moment(startDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
    const endYmd = endDate ? moment(endDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
    const empId = this.employeeMultiCtrl.value || 'all';
    const fetchKey = `${companyId}_${empId}_${startYmd}_${endYmd}`;

    if (this.lastConductFetchKey === fetchKey && this.conductTimelogs.length > 0) {
      this.matchScreenshotsToConductLogs();
      this.applyProjectFilter();
      return;
    }

    this.lastConductFetchKey = fetchKey;

    this._projectService
      .fetchConductTimelogs({
        supersee_company_id: companyId,
        requesting_user_id: loggedInUserId,
        start_date: startYmd,
        end_date: endYmd
      })
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null))
      )
      .subscribe((res: any) => {
        const rawLogs = res?.timelogs || res?.data || (Array.isArray(res) ? res : []);
        if (rawLogs && rawLogs.length > 0) {
          this.processLoadedConductLogs(rawLogs);
        } else {
          this._projectService
            .getSavedConductTimelogs(companyId, null, null, startYmd, endYmd)
            .pipe(
              takeUntil(this.destroy$),
              catchError(() => of({ timelogs: [] }))
            )
            .subscribe((savedRes: any) => {
              const savedLogs = savedRes?.timelogs || savedRes?.data || (Array.isArray(savedRes) ? savedRes : []);
              this.processLoadedConductLogs(savedLogs);
            });
        }
      });
  }

  private processReceivedPhotos(photos: any[], requestId: number): void {
    if (requestId !== this.screenshotListRequestId) {
      return;
    }

    photos = photos.map((ele: any) => {
      const timeFields =
        this.screenshotTime.getEnrichedScreenshotTime(ele.ist_time, this.shiftTimezone) ?? {};
      return {
        ...ele,
        captured_at: ele.ist_time,
        captured_time: ele.ist_time,
        ...timeFields,
      };
    });

    this.allLoadedPhotos = photos;
    this.rawPhotoList = photos;
    this.matchScreenshotsToConductLogs();
    this.applyProjectFilter();
  }

  private processLoadedConductLogs(rawLogs: any[]): void {
    // Enrich logs with userList info (name/email) so matching is robust
    const enrichedLogs = (rawLogs || []).map((log: any) => {
      let resolvedUserId = log.supersee_user_id || log.supersee_user;
      let resolvedName = log.employee_name || '';
      let resolvedEmail = log.employee_email || log.user_email || '';

      if (this.userList && this.userList.length > 0) {
        let matched: any = null;
        if (resolvedUserId) {
          const sId = String(resolvedUserId);
          matched = this.userList.find((u: any) => String(u.id ?? u.ID ?? '') === sId);
        }
        if (!matched && resolvedEmail) {
          const sEmail = resolvedEmail.toLowerCase().trim();
          matched = this.userList.find((u: any) => (u.user_email || u.email || '').toLowerCase().trim() === sEmail);
        }
        if (!matched && resolvedName) {
          const sName = resolvedName.toLowerCase().trim();
          matched = this.userList.find((u: any) => (u.display_name || u.name || '').toLowerCase().trim() === sName);
        }
        if (matched) {
          resolvedUserId = resolvedUserId || matched.id || matched.ID;
          resolvedName = resolvedName || matched.name || matched.display_name;
          resolvedEmail = resolvedEmail || matched.email || matched.user_email;
        }
      }

      return {
        ...log,
        supersee_user_id: resolvedUserId,
        employee_name: resolvedName,
        employee_email: resolvedEmail
      };
    });

    const selectedEmp = this.employeeMultiCtrl.value;
    let filteredLogs = enrichedLogs;
    if (selectedEmp) {
      const targetEmp = this.getUserfromUserList(selectedEmp);
      const targetId = String(selectedEmp);
      const targetName = (targetEmp?.name || '').trim().toLowerCase();
      const targetEmail = (targetEmp?.email || '').trim().toLowerCase();

      filteredLogs = enrichedLogs.filter((log: any) => {
        const logUid = String(log.supersee_user_id || log.supersee_user || log.user_id || '');
        if (logUid && logUid === targetId) return true;
        const logEmpName = (log.employee_name || '').trim().toLowerCase();
        if (targetName && logEmpName && targetName === logEmpName) return true;
        const logEmpEmail = (log.employee_email || '').trim().toLowerCase();
        if (targetEmail && logEmpEmail && targetEmail === logEmpEmail) return true;
        return false;
      });
    }

    this.conductTimelogs = filteredLogs;

    // Build available projects
    const projMap = new Map<string, { totalSeconds: number }>();
    (filteredLogs || []).forEach((log: any) => {
      const pName = (log.project_name || 'General Project').trim();
      if (!pName) return;
      const dur = parseInt(String(log.duration_time_second || 0), 10) || 0;
      const cur = projMap.get(pName) || { totalSeconds: 0 };
      cur.totalSeconds += dur;
      projMap.set(pName, cur);
    });

    this.availableProjects = Array.from(projMap.entries())
      .map(([name, data]) => ({
        name,
        totalSeconds: data.totalSeconds,
        totalHours: this.formatHoursMinutes(data.totalSeconds)
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    // Reset project filter if current selection doesn't exist
    const currentProj = (this.projectFilterCtrl.value || 'all').toLowerCase();
    if (currentProj !== 'all') {
      const exists = this.availableProjects.some(p => p.name.toLowerCase() === currentProj);
      if (!exists) {
        this.projectFilterCtrl.setValue('all', { emitEvent: false });
        this.updateQueryParams();
      }
    }

    // Parse intervals
    this.parsedConductIntervals = (filteredLogs || [])
      .map((l: any) => this.parseConductInterval(l))
      .filter((x): x is { startUtcMs: number; endUtcMs: number; projectName: string; taskTitle: string; raw: any } => x !== null);

    this.matchScreenshotsToConductLogs();
    this.applyProjectFilter();
  }

  private formatHoursMinutes(totalSecs: number): string {
    const s = Math.max(0, Math.floor(totalSecs || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return `${h}h ${String(m).padStart(2, '0')}m`;
  }

  private parseConductInterval(log: any): { startUtcMs: number; endUtcMs: number; projectName: string; taskTitle: string; raw: any } | null {
    if (!log) return null;
    const fromStr = String(log.from_time || '').trim();
    const endStr = String(log.end_time || '').trim();
    const rawDate = String(log.log_date || log.date || '').trim();
    let logDate = rawDate ? moment(rawDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');

    let startUtc: DateTime | null = fromStr ? this.parseConductTimeToUtc(fromStr, logDate) : null;
    let endUtc: DateTime | null = endStr ? this.parseConductTimeToUtc(endStr, logDate) : null;
    const durSec = parseInt(String(log.duration_time_second || 0), 10) || 0;

    if (startUtc?.isValid && endUtc?.isValid) {
      let sMs = startUtc.toMillis();
      let eMs = endUtc.toMillis();
      if (eMs <= sMs && durSec > 0) eMs = sMs + durSec * 1000;
      if (eMs > sMs) {
        return {
          startUtcMs: sMs,
          endUtcMs: eMs,
          projectName: (log.project_name || 'General Project').trim(),
          taskTitle: (log.task_title || 'General Task').trim(),
          raw: log
        };
      }
    }

    if (startUtc?.isValid && durSec > 0) {
      const sMs = startUtc.toMillis();
      return {
        startUtcMs: sMs,
        endUtcMs: sMs + durSec * 1000,
        projectName: (log.project_name || 'General Project').trim(),
        taskTitle: (log.task_title || 'General Task').trim(),
        raw: log
      };
    }

    if (endUtc?.isValid && durSec > 0) {
      const eMs = endUtc.toMillis();
      return {
        startUtcMs: eMs - durSec * 1000,
        endUtcMs: eMs,
        projectName: (log.project_name || 'General Project').trim(),
        taskTitle: (log.task_title || 'General Task').trim(),
        raw: log
      };
    }

    return null;
  }

  private parseConductTimeToUtc(timeStr: string, logDate: string): DateTime | null {
    if (!timeStr) return null;
    const tz = this.shiftTimezone && this.screenshotTime.isValidIanaZone(this.shiftTimezone)
      ? this.shiftTimezone
      : Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (timeStr.includes('T') || (timeStr.includes('-') && timeStr.length > 10)) {
      const dt = this.screenshotTime.parseCaptureToUtc(timeStr, this.shiftTimezone);
      if (dt?.isValid) return dt;
    }

    const isAmPm = /am|pm/i.test(timeStr);
    if (isAmPm) {
      const m = moment(`${logDate} ${timeStr}`, [
        'YYYY-MM-DD hh:mm:ss A',
        'YYYY-MM-DD hh:mm A',
        'YYYY-MM-DD h:mm:ss A',
        'YYYY-MM-DD h:mm A'
      ]);
      if (m.isValid()) {
        const iso = m.format('YYYY-MM-DDTHH:mm:ss');
        const dt = DateTime.fromISO(iso, { zone: tz });
        if (dt.isValid) return dt.toUTC();
      }
    }

    const m24 = moment(`${logDate} ${timeStr}`, [
      'YYYY-MM-DD HH:mm:ss',
      'YYYY-MM-DD HH:mm',
      'YYYY-MM-DD H:mm:ss',
      'YYYY-MM-DD H:mm'
    ]);
    if (m24.isValid()) {
      const iso = m24.format('YYYY-MM-DDTHH:mm:ss');
      const dt = DateTime.fromISO(iso, { zone: tz });
      if (dt.isValid) return dt.toUTC();
    }

    const fullStr = `${logDate} ${timeStr}`;
    return this.screenshotTime.parseCaptureToUtc(fullStr, this.shiftTimezone);
  }

  private matchScreenshotsToConductLogs(): void {
    const list = this.allLoadedPhotos && this.allLoadedPhotos.length > 0 ? this.allLoadedPhotos : this.rawPhotoList;
    if (!this.parsedConductIntervals || this.parsedConductIntervals.length === 0) {
      (list || []).forEach(ss => {
        ss._conductProject = null;
        ss._conductTask = null;
        ss._isConductTracked = false;
        ss._conductLog = null;
      });
      return;
    }

    (list || []).forEach(ss => {
      const timeVal = ss.ist_time || ss.captured_at || ss.time;
      const parsedUtc = timeVal ? this.screenshotTime.parseCaptureToUtc(timeVal, this.shiftTimezone) : null;
      const t = parsedUtc?.isValid ? parsedUtc.toMillis() : null;
      if (!t) {
        ss._conductProject = null;
        ss._conductTask = null;
        ss._isConductTracked = false;
        ss._conductLog = null;
        return;
      }

      const photoUserId = String(ss.user_id || ss.author || '');
      const user = photoUserId ? this.getUserfromUserList(photoUserId) : null;
      const userName = (user?.name || user?.display_name || '').trim().toLowerCase();
      const userEmail = (user?.email || user?.user_email || '').trim().toLowerCase();

      const matched = this.parsedConductIntervals.find(p => {
        if (t < p.startUtcMs || t > p.endUtcMs) return false;
        if (photoUserId) {
          const logUid = String(p.raw.supersee_user_id || p.raw.supersee_user || p.raw.user_id || '');
          if (logUid && logUid === photoUserId) return true;
          const logEmpName = (p.raw.employee_name || '').trim().toLowerCase();
          if (userName && logEmpName && userName === logEmpName) return true;
          const logEmpEmail = (p.raw.employee_email || '').trim().toLowerCase();
          if (userEmail && logEmpEmail && userEmail === logEmpEmail) return true;
          return false;
        }
        return true;
      });

      if (matched) {
        ss._conductProject = matched.projectName;
        ss._conductTask = matched.taskTitle;
        ss._isConductTracked = true;
        ss._conductLog = matched.raw;
      } else {
        ss._conductProject = null;
        ss._conductTask = null;
        ss._isConductTracked = false;
        ss._conductLog = null;
      }
    });
  }

  applyProjectFilter(): void {
    const selectedProj = (this.projectFilterCtrl.value || 'all').trim().toLowerCase();
    if (selectedProj === 'all') {
      this.total_ss = this.serverTotalSs || this.rawPhotoList.length;
      this.total_pages = Math.max(1, Math.ceil(this.total_ss / (this.pageSize || 25)));
      this.pagedList = this.buildPageNumberList(this.total_pages);
      if (this.allLoadedPhotos && this.allLoadedPhotos.length > (this.pageSize || 25)) {
        this.PhotoList = this.allLoadedPhotos.slice(this.offset, this.offset + (this.pageSize || 25));
      } else {
        this.PhotoList = this.rawPhotoList;
      }
    } else {
      const source = this.allLoadedPhotos && this.allLoadedPhotos.length > 0 ? this.allLoadedPhotos : this.rawPhotoList;
      this.filteredPhotos = (source || []).filter(s =>
        (s._conductProject || '').trim().toLowerCase() === selectedProj
      );
      this.total_ss = this.filteredPhotos.length;
      this.total_pages = Math.max(1, Math.ceil(this.total_ss / (this.pageSize || 25)));
      this.pagedList = this.buildPageNumberList(this.total_pages);
      this.PhotoList = this.filteredPhotos.slice(this.offset, this.offset + (this.pageSize || 25));
    }
  }

  onProjectFilterChange(projVal: string): void {
    this.projectFilterCtrl.setValue(projVal || 'all');
    this.currentPage = 1;
    this.offset = 0;
    if (this.EmpPaginator) {
      this.EmpPaginator.firstPage();
    }
    this.updateQueryParams();

    // If changing to a specific project and we don't have all photos loaded, fetch remaining pages
    if (projVal !== 'all' && (!this.allLoadedPhotos || this.allLoadedPhotos.length <= (this.pageSize || 25))) {
      this.getLastScreenshots();
    } else {
      this.applyProjectFilter();
    }
  }

  get projectFilterTriggerLabel(): string {
    const val = (this.projectFilterCtrl.value || 'all').toLowerCase();
    if (val === 'all') return 'All projects';
    const found = this.availableProjects.find(p => p.name.toLowerCase() === val);
    return found ? found.name : val;
  }

  openConductDrawerForScreenshot(image: any): void {
    if (!image) return;
    const log = image._conductLog;
    const user = this.getUserfromUserList(image.user_id);
    const date = image.ist_time ? new Date(image.ist_time) : (this.selectedDateAndTime?.startDate?.toDate() || new Date());

    const userLogs = (this.conductTimelogs || []).filter((l: any) => {
      const uid = String(l.supersee_user_id || l.user_id || '');
      if (uid && String(image.user_id) === uid) return true;
      if (user?.name && l.employee_name && user.name.trim().toLowerCase() === l.employee_name.trim().toLowerCase()) return true;
      if (user?.email && l.employee_email && user.email.trim().toLowerCase() === l.employee_email.trim().toLowerCase()) return true;
      return false;
    });

    const group = {
      supersee_user_id: image.user_id,
      employee_name: user?.name || log?.employee_name || 'User',
      employee_email: user?.email || log?.employee_email || '',
      logs: userLogs.length > 0 ? userLogs : (log ? [log] : [])
    };

    this._sharedServices.componentDrawerSubject.next({
      componentName: 'conduct-timelogs',
      componentAction: 'open',
      componentWidth: 680,
      componentData: {
        group,
        groups: [group],
        currentIndex: 0,
        selectedDate: date,
        startDate: date,
        endDate: date,
        users: user ? [user] : this.userList
      }
    });
  }

  getTriggerChipClass(type: string | undefined): string {
    switch (type) {
      case 'app-changed':
        return 'screenshot-type-chip--appswitch';
      case 'admin-request':
        return 'screenshot-type-chip--ondemand';
      default:
        return 'screenshot-type-chip--periodic';
    }
  }

  onScreenshotCardClick(image: any): void {
    if (!this.PhotoList?.length) {
      return;
    }
    const startIndex = Math.max(
      0,
      this.PhotoList.findIndex((p: any) => p.id === image.id)
    );
    this.screenshotFancybox.open({
      items: this.PhotoList,
      startIndex,
      userList: this.userList,
      showEmployee: true,
      showWindow: false,
      shiftTimezone: this.shiftTimezone,
    });
  }

  removeScreenShot(imageInfo: any) {
    const userName = this._sharedServices.getUserfromUserList(imageInfo.user_id, this.userList).name || 'Unknown User';
    // Handle dummy screenshots differently
    if (this.isShowingDemoData) {
      this.sweetAlertService
        .showScreenshotConfirmation(imageInfo, userName)
        .then((result) => {
          if (result.isConfirmed) {
            this.sweetAlertService.showAlert('Screenshot deleted', 'The screenshot was permanently removed.', 'success');
            // Regenerate dummy screenshots
            this.generateDummyScreenshots();
          }
        });
      return;
    }

    this.sweetAlertService
      .showScreenshotConfirmation(imageInfo, userName)
      .then((result) => {
        if (result.isConfirmed) {
          // Call API to delete screenshot
          this._projectService.removeScreenshots(imageInfo.id).subscribe(
            () => {
              this.sweetAlertService.showAlert('Screenshot deleted', 'The screenshot was permanently removed.', 'success');
              this.getLastScreenshots();
            },
            (error) => {
              this.sweetAlertService.showAlert('Error!', 'Failed to delete screenshot.', 'error');
            }
          );
        }
      });
  }

  downloadScreenShot(imageInfo: any) {
    this.sweetAlertService.showDownloadProcess();
    const imageUrl = imageInfo.image_url;
    this.http.get(imageUrl + '?cacheblock=true', { responseType: 'blob' }).subscribe((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.getUserfromUserList(imageInfo.user_id)?.name}_${imageInfo.captured_time}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      swal.close();
    }, (error) => {
      console.error("Error downloading screenshot:", error);
      swal.close();
      this.sweetAlertService.showAlert('Error!', 'Failed to download screenshot.', 'error');
    });
  }

  downloadAllScreenshots() {
    this.sweetAlertService.showDownloadProcess();
    const zip = new JSZip();
    const urls: string[] = [];
    const times: string[] = [];
    const users: string[] = [];

    if (this.PhotoList && this.PhotoList.length) {
      this.PhotoList.forEach((photo: any) => {
        urls.push(photo.image_url);
        times.push(photo.captured_time);
        users.push(photo.user_id);
      });
    }

    const promises = urls.map((url, index) =>
      this.http.get(url + '?cacheblock=true', { responseType: 'blob' }).toPromise().then((blob) => {
        const userName = this.getUserfromUserList(users[index])?.name || 'Unknown';
        zip.file(`${userName}_${times[index]}.png`, blob);
      })
    );

    Promise.all(promises).then(() => {
      zip.generateAsync({ type: 'blob' }).then((content) => {
        saveAs(content, 'screenshots.zip');
        swal.close();
      });
    }).catch((error) => {
      console.error("Error downloading screenshots:", error);
      swal.close();
      this.sweetAlertService.showAlert('Error!', 'Failed to download screenshots.', 'error');
    });
  }

  generateDummyScreenshots() {
    // Populate userList with dummy users if not already populated
    if (this.userList.length === 0 || !this.userList.some(u => u.id >= 1000 && u.id <= 1004)) {
      const dummyUsers = this._dummyDataService.getDataByParams<any>('EmployeeList').data;
      if (dummyUsers && dummyUsers.length > 0) {
        this.userList = dummyUsers;
        this.filteredUserList = dummyUsers.slice();
      }
    }

    const dummyScreenshots: any[] = [];
    const screenshotTypes = ['periodic', 'manual', 'app-changed'];
    const apps = ['Google Chrome', 'VS Code', 'Slack', 'Discord', 'Figma', 'Microsoft Edge', 'Adobe Premiere Pro'];
    // Use the actual dummy user IDs from the TourConfigService
    const dummyUserIds = [1000, 1001, 1002, 1003, 1004];

    for (let i = 0; i < this.pageSize; i++) {
      const userId = dummyUserIds[i % dummyUserIds.length];
      const now = moment();
      const capturedTime = now.clone().subtract(Math.floor(Math.random() * 120), 'minutes');
      const ct = capturedTime.format('YYYY-MM-DD HH:mm:ss');

      dummyScreenshots.push({
        id: `dummy_${i}`,
        user_id: userId,
        image_url: this.getDummyScreenshotImage(i),
        ist_time: ct,
        captured_at: capturedTime.toISOString(),
        captured_time: ct,
        ...(this.screenshotTime.getEnrichedScreenshotTime(ct, this.shiftTimezone) ?? {}),
        type: screenshotTypes[i % screenshotTypes.length],
        app_name: apps[i % apps.length]
      });
    }

    this.PhotoList = dummyScreenshots;
    this.total_ss = dummyScreenshots.length;
  }

  getDummyScreenshotImage(index: number): string {
    const screenshotImages = [
      'assets/screenshots/1.png',
      'assets/screenshots/2.png',
      'assets/screenshots/3.png',
      'assets/screenshots/4.png',
      'assets/screenshots/5.png',
      'assets/screenshots/6.png',
      'assets/screenshots/7.png',
      'assets/screenshots/8.png',
    ];
    return screenshotImages[index % screenshotImages.length];
  }

  loggedInUserDetails() {
    this._projectService.getUser().subscribe((data: any) => {
      if (data && data?.meta) {
        this.serverDate = data?.meta?.today_date || new Date();
        const companyId = this.tokenEncryptionService.getEncryptedItem<any>('logedInUser')?.company_id ?? data?.company_id;
        this._sharedServices.getValidationDateRangeByCompany(this.serverDate, companyId, {
          lookbackDays: this.entitlementsService.getScreenshotsRetentionDays(),
        }).subscribe((range) => {
          this.minDate = range.minDate;
          this.maxDate = range.maxDate;
          this.ensureLocalTodayIsSelectable();
          this.validateCurrentDateRange();
          this.patchScreenshotDateRangeFromMoments();
        });
      }
    });
  }

  /**
   * Company shift TZ / WP today_date can still be "yesterday" after local midnight
   * (typical for trial companies on UTC or US shifts when the admin is in IST).
   * The picker must still allow local today so today's captures are not excluded.
   */
  private ensureLocalTodayIsSelectable(): void {
    const localTodayEnd = moment().endOf('day');
    if (!this.maxDate || moment(this.maxDate).isBefore(localTodayEnd, 'day')) {
      this.maxDate = localTodayEnd.toDate();
    }
    this.refreshScreenshotMatDateBounds();
  }

  private defaultScreenshotDateRange(): { startDate: moment.Moment; endDate: moment.Moment } {
    const now = moment();
    let newStart = now.clone().subtract(30, 'days').startOf('day');
    let newEnd = now.clone().endOf('day');

    if (this.minDate && newStart.isBefore(moment(this.minDate), 'day')) {
      newStart = moment(this.minDate).startOf('day');
    }
    if (this.maxDate && newEnd.isAfter(moment(this.maxDate), 'day')) {
      newEnd = moment(this.maxDate).endOf('day');
    }

    return { startDate: newStart, endDate: newEnd };
  }

  /**
   * True when the range looks like the default 30-day window ending yesterday —
   * the stale URL written after a timezone off-by-one clamp.
   */
  private isTimezoneShiftedDefaultRange(start: moment.Moment, end: moment.Moment): boolean {
    if (!start?.isValid?.() || !end?.isValid?.()) {
      return false;
    }
    const localToday = moment().startOf('day');
    const endDay = end.clone().startOf('day');
    if (localToday.diff(endDay, 'days') !== 1) {
      return false;
    }
    const span = endDay.diff(start.clone().startOf('day'), 'days');
    return span >= 28 && span <= 31;
  }

  private applyDefaultScreenshotDateRange(): void {
    this.selectedDateAndTime = this.defaultScreenshotDateRange();
    this.screenshotStartTimeStr = '00:00:00';
    this.screenshotEndTimeStr = '23:59:59';
    this.change(null);
  }

  private validateCurrentDateRange() {
    if (!this.selectedDateAndTime?.startDate || !this.selectedDateAndTime?.endDate) return;

    const start = this.selectedDateAndTime.startDate.clone();
    const end = this.selectedDateAndTime.endDate.clone();

    if (this.isTimezoneShiftedDefaultRange(start, end)) {
      this.applyDefaultScreenshotDateRange();
      return;
    }

    let isInvalid = false;

    if (this.maxDate) {
      const maxMoment = moment(this.maxDate).endOf('day');
      if (start.isAfter(maxMoment) || end.isAfter(maxMoment)) {
        isInvalid = true;
      }
    }

    if (this.minDate) {
      const minMoment = moment(this.minDate).startOf('day');
      if (start.isBefore(minMoment) || end.isBefore(minMoment)) {
        isInvalid = true;
      }
    }

    if (isInvalid) {
      this.applyDefaultScreenshotDateRange();
    }
  }

}

